import ReactGA from 'react-ga4';

/**
 * Analytics Service - Google Analytics 4 Integration
 *
 * Provides privacy-respecting analytics tracking for waitlist and other user interactions.
 *
 * Privacy Features:
 * - Respects Do Not Track (DNT) browser setting
 * - Allows user opt-out via localStorage
 * - Does not track PII (names, emails, addresses)
 * - Anonymizes user IDs
 *
 * Usage:
 * ```typescript
 * import { analytics } from '@/services/analytics';
 *
 * // Track an event
 * analytics.track('waitlist_join_clicked', { fair_id: 123, position: 5 });
 *
 * // Identify user (anonymized)
 * analytics.identify(userId);
 *
 * // Track page view
 * analytics.page('/waitlist');
 * ```
 */

// Check if user has opted out of analytics
const hasOptedOut = (): boolean => {
  try {
    return localStorage.getItem('analytics_opt_out') === 'true';
  } catch {
    return false;
  }
};

// Check if Do Not Track is enabled
const isDNTEnabled = (): boolean => {
  if (typeof window === 'undefined') return false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dnt = navigator.doNotTrack || (window as any).doNotTrack || (navigator as any).msDoNotTrack;
  return dnt === '1' || dnt === 'yes';
};

// Check if cookie consent has been given (Issue #1310 - GDPR/CCPA compliance)
const hasCookieConsent = (): boolean => {
  try {
    const consentGiven = localStorage.getItem('cookie_consent_given') === 'true';
    const analyticsConsent = localStorage.getItem('cookie_consent_analytics') === 'true';
    return consentGiven && analyticsConsent;
  } catch {
    return false;
  }
};

// Check if analytics should be disabled
const shouldDisableAnalytics = (): boolean => {
  return hasOptedOut() || isDNTEnabled() || !hasCookieConsent() || import.meta.env.DEV;
};

// Get environment variable helper
const getEnvVar = (key: string, fallback: string = ''): string => {
  return import.meta.env[key] || fallback;
};

/**
 * Analytics service interface
 */
export interface AnalyticsService {
  /**
   * Initialize analytics with measurement ID
   * @param measurementId - Google Analytics 4 Measurement ID (G-XXXXXXXXXX)
   */
  initialize(measurementId?: string): void;

  /**
   * Track a custom event
   * @param eventName - Name of the event (e.g., 'waitlist_join_clicked')
   * @param properties - Event properties (e.g., { fair_id: 123, position: 5 })
   */
  track(eventName: string, properties?: Record<string, unknown>): void;

  /**
   * Identify a user (anonymized)
   * @param userId - User ID (will be hashed for privacy)
   * @param traits - User traits (non-PII only)
   */
  identify(userId: string, traits?: Record<string, unknown>): void;

  /**
   * Track a page view
   * @param path - Page path (e.g., '/waitlist')
   * @param properties - Additional properties
   */
  page(path: string, properties?: Record<string, unknown>): void;

  /**
   * Opt out of analytics tracking
   */
  optOut(): void;

  /**
   * Opt back in to analytics tracking
   */
  optIn(): void;

  /**
   * Check if analytics is enabled
   */
  isEnabled(): boolean;
}

class GoogleAnalyticsService implements AnalyticsService {
  private initialized = false;
  private measurementId?: string;

  initialize(measurementId?: string): void {
    const id = measurementId || getEnvVar('VITE_GA4_MEASUREMENT_ID', '');

    if (!id) {
      console.warn('[Analytics] No measurement ID provided. Analytics will not be initialized.');
      return;
    }

    // Validate GA4 measurement ID format (G-XXXXXXXXXX)
    const ga4Pattern = /^G-[A-Z0-9]{10,}$/;
    if (!ga4Pattern.test(id)) {
      console.error(`[Analytics] Invalid GA4 measurement ID format: ${id}. Expected format: G-XXXXXXXXXX`);
      return;
    }

    if (shouldDisableAnalytics()) {
      if (import.meta.env.DEV) {
        console.debug('[Analytics] Analytics disabled (opt-out, DNT, or dev mode)');
      }
      return;
    }

    try {
      ReactGA.initialize(id, {
        gaOptions: {
          anonymizeIp: true,
        },
      });
      this.initialized = true;
      this.measurementId = id;
      if (import.meta.env.DEV) {
        console.debug('[Analytics] Initialized successfully');
      }
    } catch (error) {
      console.error('[Analytics] Failed to initialize:', error);
    }
  }

  track(eventName: string, properties?: Record<string, unknown>): void {
    if (!this.initialized || shouldDisableAnalytics()) {
      return;
    }

    try {
      ReactGA.event(eventName, properties);
      if (import.meta.env.DEV) {
        console.debug(`[Analytics] Event tracked: ${eventName}`, properties);
      }
    } catch (error) {
      console.error('[Analytics] Failed to track event:', error);
    }
  }

  identify(userId: string, traits?: Record<string, unknown>): void {
    if (!this.initialized || shouldDisableAnalytics()) {
      return;
    }

    // Hash user ID asynchronously for privacy
    this.hashUserId(userId)
      .then((anonymizedUserId) => {
        try {
          ReactGA.set({ userId: anonymizedUserId, ...traits });
          if (import.meta.env.DEV) {
            console.debug('[Analytics] User identified (anonymized)');
          }
        } catch (error) {
          console.error('[Analytics] Failed to identify user:', error);
        }
      })
      .catch((error) => {
        console.error('[Analytics] Failed to hash user ID:', error);
      });
  }

  page(path: string, properties?: Record<string, unknown>): void {
    if (!this.initialized || shouldDisableAnalytics()) {
      return;
    }

    try {
      ReactGA.send({ hitType: 'pageview', page: path, ...properties });
      if (import.meta.env.DEV) {
        console.debug(`[Analytics] Page view tracked: ${path}`, properties);
      }
    } catch (error) {
      console.error('[Analytics] Failed to track page view:', error);
    }
  }

  optOut(): void {
    try {
      localStorage.setItem('analytics_opt_out', 'true');
      if (import.meta.env.DEV) {
        console.debug('[Analytics] User opted out');
      }
    } catch (error) {
      console.error('[Analytics] Failed to opt out:', error);
    }
  }

  optIn(): void {
    try {
      localStorage.removeItem('analytics_opt_out');
      if (import.meta.env.DEV) {
        console.debug('[Analytics] User opted in');
      }
      // Reinitialize if we have a measurement ID
      if (this.measurementId) {
        this.initialize(this.measurementId);
      }
    } catch (error) {
      console.error('[Analytics] Failed to opt in:', error);
    }
  }

  isEnabled(): boolean {
    return this.initialized && !shouldDisableAnalytics();
  }

  /**
   * Cryptographically secure hash function for anonymizing user IDs
   * Uses Web Crypto API (SHA-256) for proper anonymization
   * @private
   */
  private async hashUserId(userId: string): Promise<string> {
    try {
      // Use Web Crypto API for proper hashing
      const encoder = new TextEncoder();
      const data = encoder.encode(userId);
      const hashBuffer = await crypto.subtle.digest('SHA-256', data);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

      // Return first 16 characters for readability while maintaining uniqueness
      return `user_${hashHex.substring(0, 16)}`;
    } catch (error) {
      // Fallback: Don't send user ID if crypto API fails
      console.error('[Analytics] Failed to hash user ID:', error);
      return 'user_anonymous';
    }
  }
}

// Export singleton instance
export const analytics: AnalyticsService = new GoogleAnalyticsService();

/**
 * Dynamically initialize Google Analytics (gtag.js) without page reload
 * Used by cookie consent banner and index.html to load analytics after consent
 *
 * @returns true if analytics was initialized, false if already initialized or blocked by DNT
 */
export function initializeGtagDynamically(): boolean {
  try {
    // Check if gtag is already loaded
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (typeof (window as any).gtag !== 'undefined') {
      console.log('[Analytics] gtag already initialized');
      return false;
    }

    // Check Do Not Track
    const dntEnabled = navigator.doNotTrack === '1' ||
                     // eslint-disable-next-line @typescript-eslint/no-explicit-any
                     (window as any).doNotTrack === '1' ||
                     // eslint-disable-next-line @typescript-eslint/no-explicit-any
                     (navigator as any).msDoNotTrack === '1';

    if (dntEnabled) {
      console.log('[Analytics] Do Not Track is enabled - analytics disabled');
      return false;
    }

    // Get measurement ID from environment variable
    const measurementId = getEnvVar('VITE_GA4_MEASUREMENT_ID', 'G-QEMHGM7K7Q');

    // Dynamically load gtag.js
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    document.head.appendChild(script);

    // Initialize gtag (must be global for Google Analytics to work)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).dataLayer = (window as any).dataLayer || [];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, prefer-rest-params
    (window as any).gtag = function(){(window as any).dataLayer.push(arguments);};
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).gtag('js', new Date());
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).gtag('config', measurementId, {
      'anonymize_ip': true
    });

    console.log('[Analytics] gtag initialized successfully after consent');
    return true;
  } catch (error) {
    console.error('[Analytics] Failed to initialize gtag:', error);
    return false;
  }
}

// Export event names as constants for type safety
export const ANALYTICS_EVENTS = {
  // Waitlist Joining
  WAITLIST_JOIN_CLICKED: 'waitlist_join_clicked',
  WAITLIST_JOIN_SUCCESS: 'waitlist_join_success',
  WAITLIST_JOIN_FAILED: 'waitlist_join_failed',

  // Slot Offers
  SLOT_OFFER_VIEWED: 'slot_offer_viewed',
  SLOT_OFFER_ACCEPTED: 'slot_offer_accepted',
  SLOT_OFFER_DECLINED: 'slot_offer_declined',
  SLOT_OFFER_EXPIRED: 'slot_offer_expired',

  // Position Changes
  POSITION_IMPROVED: 'position_improved',
  POSITION_WORSENED: 'position_worsened',

  // Waitlist Leaving
  WAITLIST_LEAVE_CLICKED: 'waitlist_leave_clicked',
  WAITLIST_LEAVE_SUCCESS: 'waitlist_leave_success',

  // Countdown Timer
  COUNTDOWN_URGENCY_REACHED: 'countdown_urgency_reached',
  COUNTDOWN_VIEWED: 'countdown_viewed',

  // Page Views
  WAITLIST_PAGE_VIEWED: 'waitlist_page_viewed',
  WAITLIST_MODAL_OPENED: 'waitlist_modal_opened',
  SLOT_OFFER_MODAL_OPENED: 'slot_offer_modal_opened',

  // Notifications
  NOTIFICATION_RECEIVED: 'notification_received',
  NOTIFICATION_CLICKED: 'notification_clicked',

  // Errors
  WAITLIST_ERROR: 'waitlist_error',
} as const;

export type AnalyticsEventName = typeof ANALYTICS_EVENTS[keyof typeof ANALYTICS_EVENTS];
